import { Flag, FolderKanban } from "lucide-react";
import { Link } from "react-router-dom";
import type { ProjectHealth } from "../types";
import {
  Card,
  CardHeader,
  Empty,
  formatDateShort,
  SkeletonRow,
  ViewAllLink,
} from "./_shared";

type Props = {
  projects: ProjectHealth[];
  isLoading?: boolean;
};

function progressColor(p: number): string {
  if (p >= 76) return "hsl(142,72%,29%)";
  if (p >= 51) return "hsl(210,68%,55%)";
  if (p >= 26) return "hsl(38,92%,50%)";
  return "hsl(0,84%,60%)";
}

export function ProjectHealthWidget({ projects, isLoading }: Props) {
  return (
    <Card>
      <CardHeader
        icon={
          <FolderKanban
            className="h-4 w-4 text-[hsl(180,72%,30%)]"
            strokeWidth={1.5}
          />
        }
        title="Project Health"
        count={projects.length}
        action={<ViewAllLink href="/projects" />}
      />
      {isLoading ? (
        <div>
          {[0, 1, 2, 3].map((i) => (
            <SkeletonRow key={i} height={42} />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <Empty
          icon={
            <FolderKanban
              className="h-8 w-8 text-muted-foreground opacity-40"
              strokeWidth={1.5}
            />
          }
          text="No active projects"
        />
      ) : (
        <ul className="space-y-2">
          {projects.slice(0, 8).map((p) => {
            const pct = Math.max(0, Math.min(100, p.progress ?? 0));
            const color = progressColor(pct);
            return (
              <li key={p.id}>
                <Link
                  to={`/projects/${p.id}`}
                  className="flex items-center gap-3 rounded-md p-1.5 hover:bg-[hsl(210,50%,98%)]"
                >
                  <div
                    className="min-w-0"
                    style={{ width: 180 }}
                  >
                    <div className="truncate text-[13px] font-medium text-foreground">
                      {p.name}
                    </div>
                    {p.companyName && (
                      <div className="truncate text-[11px] text-muted-foreground">
                        {p.companyName}
                      </div>
                    )}
                    {p.upcomingMilestone && (
                      <div
                        className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-muted-foreground"
                      >
                        <Flag className="h-2.5 w-2.5" strokeWidth={1.5} />
                        <span className="truncate">
                          {p.upcomingMilestone.title}
                        </span>
                        {p.upcomingMilestone.dueDate && (
                          <span>
                            · {formatDateShort(p.upcomingMilestone.dueDate)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-1 items-center gap-2">
                    <div
                      className="flex-1 overflow-hidden rounded-full"
                      style={{
                        height: 8,
                        background: "hsl(210,16%,93%)",
                      }}
                    >
                      <div
                        style={{
                          width: `${pct}%`,
                          height: "100%",
                          background: color,
                        }}
                      />
                    </div>
                    <span
                      className="text-[12px] font-medium"
                      style={{ color }}
                    >
                      {pct}%
                    </span>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-0.5">
                    <span
                      className="rounded-full px-2 py-0.5 text-[10px] font-medium"
                      style={{
                        background: "hsl(210,16%,93%)",
                        color: "hsl(215,16%,40%)",
                      }}
                    >
                      {p.status}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {p.completedTasks}/{p.taskCount}
                    </span>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
